import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Plus, Sparkles, ArrowRight, Play, Archive, Trash2, Workflow, Copy,
    CheckCircle2, Loader2, Zap, Webhook, Clock, HandMetal,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal";

/* ================================================================
   Nexus One · Orchestrations List — Phase 5-B / D2 list page
   Dark Palantir/Gotham palette.
   ================================================================ */

const TABS = [
    { key: "mine",      label: "My Orchestrations", icon: Workflow },
    { key: "templates", label: "Templates",         icon: Sparkles },
];

const STATUS_STYLES = {
    active:   { bg: "rgba(52,211,153,0.14)", border: "rgba(52,211,153,0.55)", color: "#34d399", label: "ACTIVE" },
    draft:    { bg: "rgba(148,163,184,0.14)", border: "rgba(148,163,184,0.45)", color: "#cbd5e1", label: "DRAFT" },
    archived: { bg: "rgba(245,158,11,0.14)", border: "rgba(245,158,11,0.45)", color: "#fbbf24", label: "ARCHIVED" },
};

const TRIGGER_ICON = {
    manual: HandMetal,
    webhook: Webhook,
    scheduled: Clock,
    event: Zap,
};

export default function OrchestrationsList() {
    const nav = useNavigate();
    const [tab, setTab] = useState("mine");
    const [orchs, setOrchs] = useState([]);
    const [counts, setCounts] = useState({ total: 0, active: 0, draft: 0, runs_today: 0 });
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [deletingBusy, setDeletingBusy] = useState(false);
    const [running, setRunning] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: mineData }, { data: tplData }] = await Promise.all([
                api.get("/orchestrations"),
                api.get("/orchestrations/templates"),
            ]);
            setOrchs(mineData.orchestrations || []);
            setCounts(mineData.counts || { total: 0, active: 0, draft: 0, runs_today: 0 });
            setTemplates(tplData.templates || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not load orchestrations.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const cloneTemplate = async (slug) => {
        setCreating(slug);
        try {
            const { data } = await api.post("/orchestrations", { name: "", template_slug: slug });
            const o = data.orchestration;
            toast.success("Template cloned to your drafts.");
            nav(`/app/orchestrations/${o.id}/edit`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Clone failed.");
        } finally {
            setCreating(null);
        }
    };

    const runOrch = async (o) => {
        setRunning(o.id);
        try {
            const { data } = await api.post(`/orchestrations/${o.id}/run`, { trigger_payload: {} });
            toast.success("Run started.");
            nav(`/app/orchestrations/${o.id}/runs/${data.run_id}`);
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Run failed to start.");
        } finally {
            setRunning(null);
        }
    };

    const archive = async (o) => {
        try {
            await api.post(`/orchestrations/${o.id}/archive`);
            toast.success("Archived.");
            load();
        } catch (e) { toast.error("Archive failed."); }
    };

    const doDelete = async () => {
        if (!deleting) return;
        setDeletingBusy(true);
        try {
            await api.delete(`/orchestrations/${deleting.id}`);
            toast.success("Orchestration deleted.");
            setDeleting(null);
            load();
        } catch (e) { toast.error("Delete failed."); }
        finally { setDeletingBusy(false); }
    };

    return (
        <div className="h-full overflow-y-auto atlas-scrollbar" data-testid="orchestrations-list">
            <div className="max-w-6xl mx-auto px-8 py-10">
                {/* HERO */}
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <span className="atlas-hero-eyebrow" data-testid="orch-eyebrow">
                            <Workflow className="w-3 h-3" strokeWidth={2.5} />
                            Phase 5-B · Orchestrations
                        </span>
                        <h1
                            className="atlas-text-gradient mt-4 text-[36px] sm:text-[44px] font-semibold leading-[1.05] tracking-tight"
                            data-testid="orch-heading"
                        >
                            Chain Workforce Members.
                            <br />
                            Add conditions. Approve on your terms.
                        </h1>
                        <p
                            className="mt-3 text-[14.5px] max-w-2xl leading-relaxed"
                            style={{ color: "var(--atlas-text-secondary)" }}
                        >
                            Compose multi-step workflows where your Digital Workforce hands off tasks between one another — with branching logic and human approval gates you control.
                        </p>
                    </div>
                    <button
                        onClick={() => nav("/app/orchestrations/new")}
                        data-testid="orchestration-create-btn"
                        className="atlas-btn-primary inline-flex items-center gap-1.5 rounded-lg px-4 h-11 text-[13.5px]"
                    >
                        <Plus className="w-4 h-4" strokeWidth={2.25} />
                        New orchestration
                    </button>
                </div>

                {/* KPI STRIP */}
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="orch-kpis">
                    <KpiCard label="Total" value={counts.total} testid="orch-kpi-total" />
                    <KpiCard label="Active" value={counts.active} accent="success" testid="orch-kpi-active" />
                    <KpiCard label="In draft" value={counts.draft} accent="warning" testid="orch-kpi-draft" />
                    <KpiCard label="Runs today" value={counts.runs_today} accent="primary" testid="orch-kpi-runs-today" />
                </div>

                {/* TABS */}
                <div
                    className="mt-8 flex flex-wrap items-center gap-1 border-b"
                    style={{ borderColor: "var(--atlas-border)" }}
                >
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        const active = tab === t.key;
                        return (
                            <button
                                key={t.key}
                                onClick={() => setTab(t.key)}
                                data-testid={`orch-tab-${t.key}`}
                                className="relative inline-flex items-center gap-1.5 px-4 h-10 text-[13px] font-medium transition-colors"
                                style={{ color: active ? "var(--atlas-text-primary)" : "var(--atlas-text-muted)" }}
                            >
                                <Icon
                                    className="w-3.5 h-3.5"
                                    strokeWidth={2}
                                    style={{ color: active ? "var(--atlas-primary-hover)" : "currentColor" }}
                                />
                                {t.label}
                                {active && (
                                    <span
                                        className="absolute left-2 right-2 -bottom-px h-[2px] rounded-full"
                                        style={{
                                            background: "var(--atlas-gradient-btn)",
                                            boxShadow: "0 0 12px var(--atlas-primary-glow)",
                                        }}
                                    />
                                )}
                            </button>
                        );
                    })}
                </div>

                {loading ? (
                    <div className="mt-10 text-[13px]" style={{ color: "var(--atlas-text-muted)" }}>
                        Loading orchestrations…
                    </div>
                ) : tab === "templates" ? (
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="orch-templates-grid">
                        {templates.map((t) => (
                            <TemplateCard key={t.slug} tpl={t} onUse={() => cloneTemplate(t.slug)} busy={creating === t.slug} />
                        ))}
                    </div>
                ) : orchs.length === 0 ? (
                    <EmptyState onNew={() => nav("/app/orchestrations/new")} onBrowseTemplates={() => setTab("templates")} />
                ) : (
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="orch-grid">
                        {orchs.map((o) => (
                            <OrchestrationCard
                                key={o.id}
                                o={o}
                                onRun={() => runOrch(o)}
                                onArchive={() => archive(o)}
                                onDelete={() => setDeleting(o)}
                                onEdit={() => nav(`/app/orchestrations/${o.id}/edit`)}
                                onOpenRuns={() => nav(`/app/orchestrations/${o.id}/runs`)}
                                running={running === o.id}
                            />
                        ))}
                    </div>
                )}
                <div className="h-16" />
            </div>
            <ConfirmDeleteModal
                open={!!deleting}
                title="Delete this orchestration?"
                body={deleting ? `"${deleting.name}" will be deleted. Past run history is retained. This can't be undone.` : ""}
                confirmLabel="Delete orchestration"
                busy={deletingBusy}
                onConfirm={doDelete}
                onClose={() => setDeleting(null)}
            />
        </div>
    );
}

function KpiCard({ label, value, accent = "neutral", testid }) {
    const color =
        accent === "success" ? "#34d399" :
        accent === "warning" ? "#fbbf24" :
        accent === "primary" ? "var(--atlas-primary-hover)" :
                               "var(--atlas-text-primary)";
    return (
        <div className="atlas-card p-4" data-testid={testid}>
            <div className="atlas-label" style={{ color: "var(--atlas-text-muted)" }}>{label}</div>
            <div className="mt-2 text-[26px] font-semibold tabular-nums leading-none" style={{ color }}>
                {value}
            </div>
        </div>
    );
}

function EmptyState({ onNew, onBrowseTemplates }) {
    return (
        <div className="mt-10 atlas-card p-10 text-center" data-testid="orch-empty">
            <div
                className="mx-auto w-16 h-16 rounded-2xl grid place-items-center"
                style={{
                    background:
                        "radial-gradient(circle at 30% 25%, rgba(167,139,250,0.55) 0%, rgba(139,92,246,0.22) 45%, rgba(6,182,212,0.10) 75%, transparent 100%)",
                    border: "1px solid rgba(139,92,246,0.45)",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.10), 0 0 48px -8px var(--atlas-primary-glow)",
                }}
            >
                <Workflow className="w-7 h-7" strokeWidth={1.6} style={{ color: "var(--atlas-primary-hover)" }} />
            </div>
            <div className="mt-4 text-[18px] font-semibold" style={{ color: "var(--atlas-text-primary)" }}>
                No orchestrations yet.
            </div>
            <p className="mt-2 text-[13px] max-w-md mx-auto" style={{ color: "var(--atlas-text-tertiary)" }}>
                Chain your Workforce Members together with conditional branches and approval gates. Start blank or clone a template.
            </p>
            <div className="mt-5 flex items-center justify-center gap-2">
                <button
                    onClick={onNew}
                    className="atlas-btn-primary inline-flex items-center gap-1 rounded-lg px-4 h-10 text-[13px]"
                    data-testid="orch-empty-new"
                >
                    Blank orchestration <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                </button>
                <button
                    onClick={onBrowseTemplates}
                    className="atlas-btn-ghost inline-flex items-center gap-1 rounded-lg px-4 h-10 text-[13px]"
                    data-testid="orch-empty-templates"
                >
                    Browse templates
                </button>
            </div>
        </div>
    );
}

function OrchestrationCard({ o, onRun, onArchive, onDelete, onEdit, onOpenRuns, running }) {
    const stStyle = STATUS_STYLES[o.status] || STATUS_STYLES.draft;
    const nodeCount = (o.nodes || []).length;
    const TriggerIcon = TRIGGER_ICON[(o.trigger || {}).type] || Zap;
    return (
        <div
            className="atlas-card atlas-card--hoverable p-4 flex flex-col"
            data-testid={`orchestration-card-${o.id}`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="text-[15px] font-semibold truncate" style={{ color: "var(--atlas-text-primary)" }}>
                        {o.name}
                    </div>
                    <div className="mt-0.5 text-[10.5px] font-semibold uppercase tracking-widest flex items-center gap-1"
                        style={{ color: "var(--atlas-text-muted)" }}>
                        <TriggerIcon className="w-2.5 h-2.5" strokeWidth={2.5} />
                        {(o.trigger || {}).type || "manual"} · {nodeCount} nodes
                    </div>
                </div>
                <span
                    className="text-[9.5px] font-bold tracking-widest px-2 py-0.5 rounded-full"
                    style={{ background: stStyle.bg, border: `1px solid ${stStyle.border}`, color: stStyle.color }}
                >
                    {stStyle.label}
                </span>
            </div>
            <p className="mt-2 text-[12.5px] leading-relaxed line-clamp-3"
                style={{ color: "var(--atlas-text-tertiary)" }}>
                {o.description || <i style={{ color: "var(--atlas-text-muted)" }}>No description.</i>}
            </p>
            <div className="mt-auto pt-3 flex items-center justify-between gap-2"
                style={{ borderTop: "1px solid var(--atlas-border)" }}>
                <button onClick={onOpenRuns}
                    className="text-[12px] font-medium inline-flex items-center gap-1"
                    style={{ color: "var(--atlas-primary-hover)" }}
                    data-testid={`orch-open-runs-${o.id}`}>
                    Runs <ArrowRight className="w-3 h-3" strokeWidth={2} />
                </button>
                <div className="flex items-center gap-1">
                    {o.status === "active" && (
                        <button onClick={onRun} disabled={running}
                            className="atlas-btn-primary inline-flex items-center gap-1 rounded-md px-2.5 h-7 text-[11.5px]"
                            data-testid={`orch-run-${o.id}`}>
                            {running ? <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2}/> : <Play className="w-3 h-3" strokeWidth={2.5}/>}
                            Run
                        </button>
                    )}
                    <button onClick={onEdit}
                        className="atlas-btn-ghost inline-flex items-center rounded-md px-2 h-7 text-[11px]"
                        data-testid={`orch-edit-${o.id}`}>
                        Edit
                    </button>
                    {o.status !== "archived" && (
                        <button onClick={onArchive} title="Archive"
                            className="inline-flex items-center justify-center w-7 h-7 rounded-md transition-all"
                            style={{ color: "var(--atlas-text-muted)", border: "1px solid var(--atlas-border-strong)" }}
                            data-testid={`orch-archive-${o.id}`}>
                            <Archive className="w-3 h-3" strokeWidth={2} />
                        </button>
                    )}
                    <button onClick={onDelete} title="Delete"
                        className="inline-flex items-center justify-center w-7 h-7 rounded-md transition-all"
                        style={{ color: "var(--atlas-text-muted)", border: "1px solid var(--atlas-border-strong)" }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.color = "#fca5a5";
                            e.currentTarget.style.borderColor = "rgba(239,68,68,0.55)";
                            e.currentTarget.style.background = "rgba(239,68,68,0.10)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.color = "var(--atlas-text-muted)";
                            e.currentTarget.style.borderColor = "var(--atlas-border-strong)";
                            e.currentTarget.style.background = "transparent";
                        }}
                        data-testid={`orch-delete-${o.id}`}>
                        <Trash2 className="w-3 h-3" strokeWidth={2} />
                    </button>
                </div>
            </div>
        </div>
    );
}

function TemplateCard({ tpl, onUse, busy }) {
    const TriggerIcon = TRIGGER_ICON[tpl.trigger_type] || Zap;
    return (
        <div className="atlas-card atlas-card--hoverable p-4 flex flex-col" data-testid={`orch-template-${tpl.slug}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="text-[15px] font-semibold flex items-center gap-1.5"
                        style={{ color: "var(--atlas-text-primary)" }}>
                        <span>{tpl.icon}</span>
                        {tpl.name}
                    </div>
                    <div className="mt-0.5 text-[10.5px] font-semibold uppercase tracking-widest flex items-center gap-1"
                        style={{ color: "var(--atlas-text-muted)" }}>
                        <TriggerIcon className="w-2.5 h-2.5" strokeWidth={2.5} />
                        {tpl.category} · {tpl.node_count} nodes · {tpl.trigger_type}
                    </div>
                </div>
                <span className="atlas-pill atlas-pill--primary text-[9px] px-1.5 py-0.5">Template</span>
            </div>
            <p className="mt-2 text-[12.5px] leading-relaxed line-clamp-3"
                style={{ color: "var(--atlas-text-tertiary)" }}>
                {tpl.description}
            </p>
            <div className="mt-auto pt-3 flex items-center justify-end"
                style={{ borderTop: "1px solid var(--atlas-border)" }}>
                <button onClick={onUse} disabled={busy}
                    className="atlas-btn-primary inline-flex items-center gap-1 rounded-md px-3 h-8 text-[12px]"
                    data-testid={`orch-template-use-${tpl.slug}`}>
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2}/> : <Copy className="w-3.5 h-3.5" strokeWidth={2}/>}
                    Use template
                </button>
            </div>
        </div>
    );
}
